import { datadogLogs } from '@datadog/browser-logs';
import type { PaymentProcessor } from 'helpers/mixpanelEvents';
import type { AppDispatch } from 'store/create-store';
import { savedCartApi } from './savedCartApi';

// 401: rejected even without the JWT
// 404: not this browser's cart (e.g. a partner iframe)
// 409: already closed (a reload)
const EXPECTED_STATUSES = new Set<unknown>([401, 404, 409]);

// Module-level instead of a ref: the Auth0/Statsig provider swap remounts the confirmation on a full
// page load, and StrictMode runs effects twice in dev.
const completedCartIds = new Set<string>();

const errorStatus = (error: unknown): unknown =>
  error && typeof error === 'object' && 'status' in error ? error.status : undefined;

interface CompleteSavedCartOptions {
  cartId: string;
  jwt?: string;
  paymentProcessor: PaymentProcessor;
}

/** Closes the donor's saved cart after a donation, once per page view. Never throws. */
export const completeSavedCartOnce = (
  dispatch: AppDispatch,
  { cartId, jwt, paymentProcessor }: CompleteSavedCartOptions
): void => {
  if (completedCartIds.has(cartId)) return;
  completedCartIds.add(cartId);

  const complete = (withJwt?: string) =>
    dispatch(savedCartApi.endpoints.completeCart.initiate({ id: cartId, jwt: withJwt })).unwrap();

  complete(jwt)
    // A stale JWT 401s even when the guest token alone would match the cart.
    .catch(error => (jwt && errorStatus(error) === 401 ? complete() : Promise.reject(error)))
    .catch(error => {
      const status = errorStatus(error);
      datadogLogs.logger[EXPECTED_STATUSES.has(status) ? 'info' : 'warn'](
        'Saved cart complete failed',
        { cart_id: cartId, http_status: status, payment_processor: paymentProcessor }
      );
    });
};
